/**
 * The start/stop control and its state pill (ADR-036 §D-7; REQ-PEER-041).
 *
 * # Why this is not ``StatusPill``
 *
 * :func:`@/system-status/status-detail-primitives.StatusPill` renders the
 * **reported** rollup — what the Peer Manager service says is happening. This
 * renders the **configured** state — what the control plane says should happen.
 * They routinely disagree, and that disagreement is diagnostic: an interface
 * `started` in config but reporting `unhealthy` is a Consumer that will not take
 * the connection, which is precisely what an on-call engineer needs to see. A
 * shared pill would collapse the two and let a config intent read as a health
 * fact — the same family of error as painting an unknown component green.
 *
 * The two nonetheless share the palette (``--status-healthy`` for running,
 * ``--status-stale`` for the deliberate standby grey) and the REQ-UI-046
 * discipline: a distinct **glyph** and a **text label** carry the state, never
 * colour alone.
 *
 * # Contrast scoping
 *
 * ``--status-stale`` was authored for map-marker staleness against the basemap
 * and measures 1.38:1 on a light panel — below the REQ-UI-046 ≥3:1 floor. The
 * System Status panel already carries a legible panel-scoped override under
 * ``.system-status-scope`` (``globals.css``), so the peer dialog applies that
 * same class: it *is* the System Status panel's mutation surface, and it needs
 * exactly the panel's status tokens. Reusing the existing scope keeps one
 * definition of the standby grey rather than a second that could drift.
 *
 * Cross-references:
 *   - ADR-036 §D-7 (start/stop is the operator kill switch).
 *   - REQ-PEER-041 (fail-safe stopped), -044 (every flip audited).
 *   - REQ-UI-046 (state never by colour alone, WCAG-AA in both themes).
 */
import type { InterfaceState } from "@/peers/peer-catalog";

/** Display metadata per configured state (REQ-UI-046 — glyph + label + token). */
const STATE_META: Record<InterfaceState, { label: string; glyph: string; color: string }> = {
  // ▶ and ⏸ rather than two dots: the shape is what survives a monochrome
  // display and a colour-vision deficiency.
  started: { label: "Started", glyph: "▶", color: "var(--status-healthy)" },
  stopped: { label: "Stopped", glyph: "❙❙", color: "var(--status-stale)" },
};

/**
 * A pill showing an interface's or connection's **configured** state.
 *
 * # The colour is on the mark, not the label — and that is measured
 *
 * ``--status-healthy`` in light is ``#1a9960``, which sits at **3.64:1** on
 * this surface (``--bg-surface`` is pure white in light). That clears the
 * REQ-UI-046 ≥3:1 bar for a **non-text indicator** and misses the ≥4.5:1 bar
 * for **text** — so the glyph carries the hue and the label is ordinary
 * ``--text-primary`` (16.7:1 light / 14.2:1 dark). Nothing is lost: the glyph
 * and the word already carry the state, so colour was never doing the work.
 *
 * This differs from :func:`@/system-status/status-detail-primitives.StatusPill`,
 * which colours its label — that pill renders on tinted node fills and elevated
 * panel backgrounds, where the same tokens clear the text bar. Measured on this
 * surface, they do not. `ui/e2e/peer-manager.spec.ts` asserts both bars.
 */
export function StatePill({ state }: { state: InterfaceState }): React.ReactElement {
  const meta = STATE_META[state];
  return (
    <span
      data-testid={`peer-state-pill-${state}`}
      className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-meta text-text-primary"
    >
      <span
        aria-hidden="true"
        data-testid={`peer-state-glyph-${state}`}
        style={{ color: meta.color }}
      >
        {meta.glyph}
      </span>
      <span>{meta.label}</span>
    </span>
  );
}

/** Props for {@link StateToggleButton}. */
export interface StateToggleButtonProps {
  state: InterfaceState;
  onToggle: (next: InterfaceState) => void;
  pending?: boolean;
  /** ``"interface"`` / ``"connection"`` — used in the accessible name. */
  label: string;
  testId: string;
}

/**
 * The start/stop button.
 *
 * Sends the **desired** state absolutely (never a toggle verb on the wire), so a
 * double-click or a retry cannot land the flow in the state the operator did not
 * ask for. Stopping is styled as the destructive-adjacent action because
 * stopping an egress path is the consequential half — it is the kill switch.
 */
export function StateToggleButton({
  state,
  onToggle,
  pending = false,
  label,
  testId,
}: StateToggleButtonProps): React.ReactElement {
  const next: InterfaceState = state === "started" ? "stopped" : "started";
  const verb = next === "started" ? "Start" : "Stop";
  return (
    <button
      type="button"
      disabled={pending}
      onClick={() => onToggle(next)}
      data-testid={testId}
      aria-label={`${verb} ${label}`}
      className={
        next === "stopped"
          ? "rounded border border-destructive px-2 py-0.5 text-meta text-destructive hover:bg-destructive/10 disabled:opacity-50"
          : "rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated disabled:opacity-50"
      }
    >
      {pending ? "…" : verb}
    </button>
  );
}
